import React, { useState } from "react";
import { Spin, Upload } from "antd";
import { useTranslation } from "react-i18next";
import { isImageTypeSupport } from "../../../utils/helpers/support-images";
import {
  errorNotification,
} from "../../../utils/helpers/notification";
import { MediumFor } from "../../../models/medium/enum";
import {
  InboxOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { compressImage } from "../../../utils/helpers/image-compression";
import { IMedium } from "../../../models/medium/response";

const { Dragger } = Upload;

interface IProps {
  imageFor: MediumFor;
  onChange: (response: IMedium | null) => any;
  type?: "DRAGGER" | "BUTTON";
  children?: React.ReactNode;
  multiple?: boolean;
  preserveOriginal?: boolean;
}

const ImageUploader: React.FC<IProps> = ({
  imageFor,
  onChange,
  type,
  multiple,
  children,
  preserveOriginal,
}) => {
  const { t } = useTranslation();

  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const handleBeforeUpload = async (file: File) => {
    setUploading(true);

    const isImageSupport = isImageTypeSupport(file.type);
    if (!isImageSupport) {
      errorNotification(t("you_can_only_upload_images"));
      setUploading(false);
      return Upload.LIST_IGNORE;
    }

    let finalFile: File = file;

    if (!preserveOriginal && file.type !== "image/gif") {
      finalFile = await compressImage(file); 
    }

    const fileWithPreview = Object.assign(finalFile, {
      preview: URL.createObjectURL(finalFile),
    });

    setFileList([fileWithPreview]);

    const reader = new FileReader();
    reader.readAsDataURL(finalFile);
    reader.onload = () => {
      const fakeResponse: IMedium = {
        id: Math.floor(Math.random() * 100000),
        url: reader.result as string,
        type: 1,
        for: imageFor,
        extension: finalFile.name?.split(".").pop() ?? "png",
        file: finalFile,
        originalFile: file,
      };

      onChange(fakeResponse);
      setUploading(false);
    };

    reader.onerror = () => {
      errorNotification(t("upload_failed"));
      setUploading(false);
    };

    return false;
  };

  const handleRemove = () => {
    setFileList([]);
    onChange(null);
  };

  return (
    <>
      {type === "DRAGGER" ? (
        <Spin spinning={uploading}>
          <Dragger
            accept="image/*"
            name="medium"
            listType="text"
            multiple={false}
            onRemove={handleRemove}
            beforeUpload={handleBeforeUpload}
            fileList={[...fileList]}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t("select_or_drag_image")}</p>
          </Dragger>
        </Spin>
      ) : (
        <Upload
          showUploadList={false}
          accept="image/*"
          name="medium"
          listType="picture"
          multiple={false}
          onRemove={handleRemove}
          beforeUpload={handleBeforeUpload}
        >
          {uploading ? (
            <div>{uploading ? <LoadingOutlined /> : <PlusOutlined />}</div>
          ) : (
            children
          )}
        </Upload>
      )}
    </>
  );
};

export default ImageUploader;
